import { Component, ChangeDetectionStrategy, signal, linkedSignal, resource, inject, viewChild, effect } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { CustomerService } from '../customer.service';
import { ErrorService } from '../../../common/errors/error.service';
import { Customer, CustomerMasterData } from '../customer.model';
import { ModalService } from 'src/app/services/modal.service';
import { CustomerMasterdataComponent } from '../customer-masterdata/customer-masterdata.component';
import { DepartmentService } from 'src/app/services/department.service';
import { HttpErrorResponse } from '@angular/common/http';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { CustomerCompanyTableComponent } from '../customer-company-table/customer-company-table.component';


@Component({
    selector: 'app-customer-dashboard',
    templateUrl: './customer-dashboard.component.html',
    styleUrl: './customer-dashboard.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CustomerMasterdataComponent, NgbTooltip, CustomerCompanyTableComponent],
})
export class CustomerDashboardComponent {
  readonly customerMasterdataComponent = viewChild.required(CustomerMasterdataComponent);

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly toastrService = inject(ToastrService);
  private readonly customerService = inject(CustomerService);
  private readonly departmentService = inject(DepartmentService);
  public readonly modalService = inject(ModalService);
  private readonly errorService = inject(ErrorService);

  private readonly kundeid = signal(0);

  protected readonly customerResource = resource({
    params: () => this.kundeid() > 0 ? this.kundeid() : undefined,
    loader: async ({ params }) => {
      const customer = await this.customerService.getCustomer(params);
      const selectedDepartment = this.departmentService.getDepartment();
      if (selectedDepartment.id !== customer.departmentId) {
        this.departmentService.setSelectedDepartment({
          id: customer.departmentId,
          code: customer.departmentCode,
          name: customer.departmentName
        });
      }
      return customer;
    }
  });

  readonly isUpdatingCustomer = signal(false);
  readonly canSaveCustomer = signal(true);

  readonly customer = linkedSignal({
    source: this.customerResource.value,
    computation: (value) => value ?? {} as CustomerMasterData
  });

  readonly errorMessage = signal<{ message: string, location: 'storage' | 'personalStorage' | 'form' } | null>(null);

  constructor() {
    const raw = Number(this.route.snapshot.paramMap.get('kundeid'));
    if (!Number.isSafeInteger(raw) || raw <= 0) {
      this.errorService.notify('KundeId [' + this.route.snapshot.paramMap.get('kundeid') + '] er ikke et gyldigt!');
      this.router.navigate(['/customers']);
    } else {
      this.kundeid.set(raw);
    }

    effect(() => {
      const error = this.customerResource.error();
      if (error) {
        this.router.navigate(['/home']);
        console.error(error);
      }
    });
  }

  updateCanSave(isValid: boolean): void {
    this.canSaveCustomer.set(isValid);
  }

  async saveCustomer(data: CustomerMasterData): Promise<void> {
    this.isUpdatingCustomer.set(true);
    
    try {
      await this.customerService.updateCustomer(data);
      this.toastrService.success('Kunden er opdateret');
      this.errorMessage.set(null);
    } catch (e: any) {
      const httpError = e as HttpErrorResponse;
      const errorMsg = httpError.error?.message;
      const msg = (errorMsg ?? '').toString().trim();
      const lower = msg.toLowerCase();

      let location: 'storage' | 'personalStorage' | 'form' = 'form';
      if (lower.startsWith('storage')) {
        location = 'storage';
      } else if (lower.startsWith('personal storage')) {
        location = 'personalStorage';
      }

      this.errorMessage.set({
        message: errorMsg ?? '',
        location
      });

      console.error(e);
    } finally {
      this.isUpdatingCustomer.set(false);
    }
  }

  onShowCreateCompany($event: any) {
    const customerData = this.customer();
    const customer: Customer = {
      departmentId: customerData.departmentId,
      departmentCode: customerData.departmentCode,
      departmentName: customerData.departmentName,
      customerId: customerData.customerId,
      name: customerData.name,
      companies: [],
      // Dashboardet kan kun åbnes for kunder med en database.
      hasDatabase: true
    } as Customer;

    this.modalService.showCustomerCreateCompany($event, customer);
  }

  submitForm(): void {
    this.customerMasterdataComponent().submitForm();
  }
}
